/**
 * CssDevEditor — Live-SASS-Editor mit Merge-Funktion
 *
 * Features:
 *  • SASS-Syntax (eingerückt): $variablen, Nesting, &, // Kommentare
 *  • Keine {} oder ; notwendig — reine Einrückung
 *  • Live-Vorschau via <style>-Injektion
 *  • Tab / Shift+Tab Einrückung
 *  • File-Dropdown: welche Quelldatei soll aktualisiert werden?
 *  • "Übernehmen": merged Änderungen property-level in die Quelldatei
 *  • Draggable Header
 *  • Portal → außerhalb des React-Baums, immer klickbar
 *
 * Öffnen: Ctrl+Shift+E oder {} Float-Button
 */

import { useState, useRef, useEffect, useCallback, useMemo } from 'react'
import { createPortal } from 'react-dom'

// ─── Konstanten ──────────────────────────────────────────────────────────────

const STYLE_ID = '__css-dev-editor__'


// ─── SASS-Transpiler (eingerückte Syntax) ────────────────────────────────────
// Unterstützt: $variablen, Nesting, &-Referenzen, // Kommentare
// Keine {} oder ; nötig — Einrückung definiert die Struktur.

interface SassNode {
  indent: number
  text:   string
  children: SassNode[]
}

function transpileSass(input: string): { css: string; error: string | null } {
  try {
    // 1. // Kommentare entfernen
    let s = input.replace(/\/\/[^\n]*/g, '')

    // 2. $variablen sammeln und substituieren (mit und ohne abschliessendes ;)
    const vars: Record<string, string> = {}
    s = s.replace(/^\s*\$([a-zA-Z_-][\w-]*)\s*:\s*(.+)/gm, (_, k, v) => {
      vars[k] = v.trim().replace(/;$/, '').trim()
      return ''
    })
    for (const [k, v] of Object.entries(vars)) {
      s = s.replace(new RegExp(`\\$${k.replace(/[-]/g, '\\$&')}\\b`, 'g'), v)
    }

    // 3. Eingerückte Struktur als Baum parsen
    const root = buildTree(s.split('\n'))

    // 4. Baum zu flachem CSS rendern
    const css = renderSassNode(root, '').trim()
    return { css, error: null }
  } catch (e) {
    return { css: '', error: String(e) }
  }
}

/** Misst die Einrücktiefe einer Zeile (Tab = 2 Spaces) */
function indentOf(line: string): number {
  let n = 0
  for (const ch of line) {
    if (ch === ' ')  n++
    else if (ch === '\t') n += 2
    else break
  }
  return n
}

/**
 * Bestimmt ob eine Zeile eine CSS-Eigenschaft ist.
 * Heuristik: beginnt mit einem Property-Bezeichner (Buchstaben / Bindestriche / Vendor-Präfix)
 * gefolgt von ": " mit einem Wert — und startet NICHT mit Selektor-Zeichen.
 */
function isSassProperty(line: string): boolean {
  const t = line.trim()
  if (!t) return false
  // Vendor-Präfix (-webkit-, -moz-, …) oder normaler Bezeichner
  return /^-?[a-zA-Z][a-zA-Z-]*\s*:\s*\S/.test(t) &&
         !/^(&|\.|\#|\*|>|\+|~|\[)/.test(t)
}

/** Baut einen Einrückungs-Baum aus den Zeilen */
function buildTree(lines: string[]): SassNode {
  const root: SassNode = { indent: -1, text: '', children: [] }
  const stack: SassNode[] = [root]

  for (const line of lines) {
    const trimmed = line.trim()
    if (!trimmed) continue

    const indent = indentOf(line)
    const node: SassNode = { indent, text: trimmed, children: [] }

    // Stack bis zum nächsten Parent mit geringerer Einrückung zurückrollen
    while (stack.length > 1 && stack[stack.length - 1].indent >= indent) {
      stack.pop()
    }

    stack[stack.length - 1].children.push(node)
    stack.push(node)
  }

  return root
}

function resolveSassSelector(raw: string, parent: string): string {
  return raw.split(',').map(s => {
    s = s.trim()
    if (!s) return ''
    if (s.includes('&')) return s.replace(/&/g, parent)
    return parent ? `${parent} ${s}` : s
  }).filter(Boolean).join(',\n')
}

/** Rendert einen Knoten und seine Kinder rekursiv zu CSS */
function renderSassNode(node: SassNode, parentSelector: string): string {
  const parts: string[] = []

  // Kinder aufteilen in direkte Properties und verschachtelte Selektoren
  const props:     SassNode[] = node.children.filter(c =>  isSassProperty(c.text))
  const selectors: SassNode[] = node.children.filter(c => !isSassProperty(c.text))

  if (node.text) {
    // Aktueller Knoten ist ein Selektor
    const selector = resolveSassSelector(node.text, parentSelector)

    if (props.length > 0) {
      const body = props
        .map(p => {
          // Semikolon ergänzen falls nicht vorhanden
          const line = p.text.endsWith(';') ? p.text : `${p.text};`
          return `    ${line}`
        })
        .join('\n')
      parts.push(`${selector} {\n${body}\n}`)
    }

    // Verschachtelte Selektoren mit aufgelöstem Parent rekursiv rendern
    const firstSel = selector.split(',')[0].trim()
    for (const child of selectors) {
      parts.push(renderSassNode(child, firstSel))
    }
  } else {
    // Root-Knoten: nur Selektoren auf oberster Ebene ausgeben
    for (const child of selectors) {
      parts.push(renderSassNode(child, ''))
    }
  }

  return parts.join('\n\n')
}

// ─── CSS-Merger ──────────────────────────────────────────────────────────────
// Merged property-level in eine bestehende CSS-Datei:
//  • Wert vorhanden → aktualisieren
//  • Eigenschaft fehlt → einfügen
//  • Leerer Wert "prop: ;" → Eigenschaft löschen
//
// Findet Blöcke auch dann, wenn der Selektor Teil einer komma-getrennten
// Gruppe ist (z.B. ".foo:hover,\n.bar .foo:hover { … }").

interface BlockMatch {
  start: number
  end: number
  fullSelector: string  // Original-Selektor-Text (inkl. aller komma-Varianten)
  body: string
}

/**
 * Sucht in `source` den ersten Block, in dessen Selektor-Liste
 * `selector` (nach Whitespace-Normalisierung) enthalten ist.
 * Damit werden komma-getrennte Mehrfach-Selektoren korrekt gefunden.
 */
function findBlockContaining(source: string, selector: string): BlockMatch | null {
  const normalize = (s: string) => s.replace(/\s+/g, ' ').trim()
  const target    = normalize(selector)
  // [^{}]+ = alles zwischen zwei Blöcken (inkl. Kommentare), [^{}]* = flacher Body
  const blockRe   = /([^{}]+)\{([^{}]*)\}/g
  let m: RegExpExecArray | null
  while ((m = blockRe.exec(source)) !== null) {
    const selPart = m[1]
    // CSS-Kommentare aus dem Selektor-Bereich entfernen, bevor wir vergleichen.
    // Ohne das würde "/* ── Header ── */\n\n.foo {" als Selektor "/* ... */  .foo"
    // erkannt — und ".foo" als target würde keinen Match finden.
    const cleanedSel = selPart.replace(/\/\*[\s\S]*?\*\//g, '')
    const selList    = cleanedSel.split(',').map(s => normalize(s)).filter(Boolean)
    if (selList.includes(target)) {
      return {
        start:        m.index,
        end:          m.index + m[0].length,
        fullSelector: selPart.trimEnd(),
        body:         m[2],
      }
    }
  }
  return null
}

/**
 * Entfernt alle Vorkommen des exakten Selektors als isolierten Block (nicht
 * als Teil einer Komma-Gruppe) aus `source`. Damit werden angehängte Duplikate
 * bereinigt, die durch frühere fehlerhafte Merge-Durchläufe entstanden sind.
 */
function removeExactBlocks(source: string, selector: string): string {
  const normalize = (s: string) => s.replace(/\s+/g, ' ').trim()
  const target = normalize(selector)
  // Nur einfache Selektor-Blöcke (kein Komma im Selektor-Teil), am Zeilenende mit \n?
  const blockRe = /([^{}]+)\{([^{}]*)\}/g
  const matches: Array<{ start: number; end: number; isExact: boolean }> = []
  let m: RegExpExecArray | null
  while ((m = blockRe.exec(source)) !== null) {
    const cleanedSel = m[1].replace(/\/\*[\s\S]*?\*\//g, '')
    const selList    = cleanedSel.split(',').map(s => normalize(s)).filter(Boolean)
    // Nur entfernen wenn es ein Ein-Selektor-Block ist, der GENAU unserem Target entspricht
    if (selList.length === 1 && selList[0] === target) {
      matches.push({ start: m.index, end: m.index + m[0].length, isExact: true })
    }
  }
  if (matches.length === 0) return source
  // Rückwärts entfernen (damit Indizes stabil bleiben), ersten Block überspringen
  let result = source
  for (let i = matches.length - 1; i >= 1; i--) {
    const { start, end } = matches[i]
    // Auch das führende Newline mitentfernen
    const from = (result[start - 1] === '\n') ? start - 1 : start
    result = result.slice(0, from) + result.slice(end)
  }
  return result
}

function mergeCss(source: string, incoming: string): string {
  const rules = parseFlatCssRules(incoming)
  let result  = source

  for (const { selector, props } of rules) {
    // Zuerst alle Duplikate (angehängte Blöcke aus alten Merge-Fehlern) entfernen —
    // nur den ersten, kanonischen Block im File behalten.
    result = removeExactBlocks(result, selector)

    const block = findBlockContaining(result, selector)

    if (block) {
      let body = block.body

      for (const [prop, value] of props) {
        const propEsc = prop.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')

        if (!value.trim()) {
          // Eigenschaft löschen
          body = body.replace(new RegExp(`[ \\t]*${propEsc}\\s*:[^;]+;\\n?`, 'g'), '')
        } else {
          // Aktualisieren oder einfügen
          const propRe = new RegExp(`(${propEsc}\\s*:)[^;]+(;)`)
          if (propRe.test(body)) {
            body = body.replace(propRe, `$1 ${value}$2`)
          } else {
            const lastNl = body.lastIndexOf('\n')
            body = lastNl >= 0
              ? body.slice(0, lastNl) + `\n    ${prop}: ${value};` + body.slice(lastNl)
              : body + `\n    ${prop}: ${value};`
          }
        }
      }

      // Ersten Block (inkl. vollständigem Selektor) an Ort und Stelle ersetzen
      result =
        result.slice(0, block.start) +
        block.fullSelector + ' {' + body + '}' +
        result.slice(block.end)
    } else {
      // Selektor existiert noch nicht → neuen Block anhängen
      const lines = [...props.entries()]
        .filter(([, v]) => v.trim())
        .map(([p, v]) => `    ${p}: ${v};`)
        .join('\n')
      if (lines) result += `\n${selector} {\n${lines}\n}\n`
    }
  }

  return result
}

function parseFlatCssRules(css: string): Array<{ selector: string; props: Map<string, string> }> {
  const rules: Array<{ selector: string; props: Map<string, string> }> = []
  const blockRe = /([^{}]+)\{([\s\S]*?)\}/g
  let m

  while ((m = blockRe.exec(css)) !== null) {
    const selector = m[1].trim()
    if (!selector) continue
    const props = new Map<string, string>()
    const propRe = /([\w-]+)\s*:\s*([^;]*);/g
    let pm
    while ((pm = propRe.exec(m[2])) !== null) {
      props.set(pm[1].trim(), pm[2].trim())
    }
    if (props.size > 0) rules.push({ selector, props })
  }

  return rules
}

// ─── Toggle-Button ───────────────────────────────────────────────────────────

function ToggleButton({ onClick }: { onClick: () => void }) {
  return (
    <button
      title="CSS Editor (Ctrl+Shift+E)"
      onClick={onClick}
      style={{
        position: 'fixed', bottom: '1.5em', right: '1em',
        zIndex: 2147483645,
        width: '1.75em', height: '1.75em',
        borderRadius: '0.375em',
        border: '1px solid rgba(255,255,255,0.12)',
        background: 'rgba(30,30,46,0.85)',
        color: 'rgba(140,140,187,0.8)',
        fontSize: '0.75em', fontWeight: 700,
        fontFamily: 'var(--font-mono, monospace)',
        cursor: 'pointer',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        backdropFilter: 'blur(6px)',
        letterSpacing: '-0.03em',
      }}
    >
      {'{}'}
    </button>
  )
}

// ─── Haupt-Editor ─────────────────────────────────────────────────────────────

type ApplyStatus = 'idle' | 'loading' | 'success' | 'error'

const CSS_FILES = [
  { label: 'style.css',               path: 'src/style.css' },
  { label: 'sidebar.css',             path: 'src/styles/sidebar.css' },
  { label: 'ghost-content.css',       path: 'src/styles/ghost-content.css' },
  { label: 'SemanticZoomOverlay.css', path: 'src/components/SemanticZoomOverlay.css' },
]

export function CssDevEditor() {
  const [isOpen,      setIsOpen]      = useState(false)
  const [scss,        setScss]        = useState('')
  const [selectedFile, setSelectedFile] = useState(CSS_FILES[0].path)
  const [copied,      setCopied]      = useState(false)
  const [applyStatus, setApplyStatus] = useState<ApplyStatus>('idle')
  const [applyMsg,    setApplyMsg]    = useState('')

  const panelRef  = useRef<HTMLDivElement>(null)
  // Position wird direkt auf dem DOM-Element gesetzt — kein Re-render beim Drag
  // Initiale Position: rechts unten, wird beim ersten Öffnen berechnet
  const dragState = useRef({ active: false, startX: 0, startY: 0, ox: -1, oy: -1 })

  // SASS kompilieren
  const { css: compiledCss, error: scssError } = useMemo(() => transpileSass(scss), [scss])

  // Live-Injektion
  useEffect(() => {
    let el = document.getElementById(STYLE_ID) as HTMLStyleElement | null
    if (!el) {
      el = document.createElement('style')
      el.id = STYLE_ID
      document.head.appendChild(el)
    }
    el.textContent = compiledCss
  }, [compiledCss])

  // Keyboard shortcut Ctrl+Shift+E
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.ctrlKey && e.shiftKey && e.key === 'E') {
        e.preventDefault()
        setIsOpen(v => !v)
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [])

  // ── Drag — direkte DOM-Manipulation, kein setState → kein Re-render ─────────
  const onDragStart = useCallback((e: React.PointerEvent) => {
    e.currentTarget.setPointerCapture(e.pointerId)
    dragState.current.active  = true
    dragState.current.startX  = e.clientX
    dragState.current.startY  = e.clientY
    // ox/oy: aktuelle Position aus dem Panel-Element auslesen
    const el = panelRef.current
    if (el) {
      dragState.current.ox = parseInt(el.style.left, 10) || dragState.current.ox
      dragState.current.oy = parseInt(el.style.top,  10) || dragState.current.oy
    }
  }, [])

  const onDragMove = useCallback((e: React.PointerEvent) => {
    if (!dragState.current.active || !panelRef.current) return
    const x = dragState.current.ox + (e.clientX - dragState.current.startX)
    const y = dragState.current.oy + (e.clientY - dragState.current.startY)
    panelRef.current.style.left = `${x}px`
    panelRef.current.style.top  = `${y}px`
  }, [])

  const onDragEnd = useCallback(() => { dragState.current.active = false }, [])

  // ── Tab / Shift+Tab ───────────────────────────────────────────────────────
  function handleTab(e: React.KeyboardEvent<HTMLTextAreaElement>) {
    if (e.key !== 'Tab') return
    e.preventDefault()
    const el    = e.currentTarget
    const start = el.selectionStart
    const end   = el.selectionEnd
    const val   = el.value

    if (e.shiftKey) {
      // Dedent: bis zu 2 Spaces vor der aktuellen Zeile entfernen
      const lineStart = val.lastIndexOf('\n', start - 1) + 1
      const indent    = val.slice(lineStart, lineStart + 2)
      const remove    = indent.startsWith('  ') ? 2 : indent.startsWith(' ') ? 1 : 0
      if (remove > 0) {
        const next = val.slice(0, lineStart) + val.slice(lineStart + remove)
        el.value = next
        el.selectionStart = el.selectionEnd = start - remove
        setScss(next)
      }
    } else {
      // Indent: 2 Spaces einfügen
      const next = val.slice(0, start) + '  ' + val.slice(end)
      el.value = next
      el.selectionStart = el.selectionEnd = start + 2
      setScss(next)
    }
  }

  // ── Kopieren ─────────────────────────────────────────────────────────────
  async function handleCopy() {
    await navigator.clipboard.writeText(compiledCss || scss)
    setCopied(true)
    setTimeout(() => setCopied(false), 1600)
  }

  // ── Übernehmen — über separaten HTTP-Server auf Port 5174 (VM-Filesystem) ────
  // File System Access API schreibt auf den lokalen Mac, nicht auf die VM.
  // Vite watched aber das VM-Filesystem → kein HMR. Daher eigener Mini-Server.
  // vite-plugin-dev-write.ts startet http.createServer auf localhost:5174.
  const DEV_SERVER = 'http://localhost:5174'

  async function handleApply() {
    if (!compiledCss.trim()) return
    setApplyStatus('loading')
    setApplyMsg('')

    try {
      // 1. Aktuellen Dateiinhalt vom Mini-Server lesen (VM-Filesystem)
      const readRes = await fetch(`${DEV_SERVER}/read?file=${encodeURIComponent(selectedFile)}`)
      if (!readRes.ok) throw new Error(`Lesen fehlgeschlagen: ${readRes.status}`)
      const currentContent = await readRes.text()

      // 2. CSS property-level mergen
      const merged = mergeCss(currentContent, compiledCss)

      // 3. Zurückschreiben → Vite erkennt Änderung und triggert HMR
      const writeRes = await fetch(`${DEV_SERVER}/write`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ file: selectedFile, content: merged }),
      })
      if (!writeRes.ok) throw new Error(`Schreiben fehlgeschlagen: ${writeRes.status}`)

      // 4. Preview-Style leeren — Vite HMR übernimmt jetzt
      const previewEl = document.getElementById(STYLE_ID) as HTMLStyleElement | null
      if (previewEl) previewEl.textContent = ''
      setScss('')

      setApplyStatus('success')
      setApplyMsg(`✓ ${selectedFile.split('/').pop()} aktualisiert`)
    } catch (e) {
      setApplyStatus('error')
      setApplyMsg(String(e))
    }

    setTimeout(() => { setApplyStatus('idle'); setApplyMsg('') }, 3000)
  }

  // ── Render ────────────────────────────────────────────────────────────────
  return createPortal(
    <>
      {isOpen && (
        <div
          ref={el => {
            (panelRef as any).current = el
            // Position beim ersten Öffnen berechnen (rechts unten)
            if (el && dragState.current.ox < 0) {
              const w = el.offsetWidth || 480
              const h = el.offsetHeight || 500
              dragState.current.ox = window.innerWidth - w - 16
              dragState.current.oy = window.innerHeight - h - 48
              el.style.left = `${dragState.current.ox}px`
              el.style.top  = `${dragState.current.oy}px`
            }
          }}
          style={{
            position: 'fixed',
            left: dragState.current.ox >= 0 ? dragState.current.ox : undefined,
            top:  dragState.current.oy >= 0 ? dragState.current.oy : undefined,
            right: dragState.current.ox < 0 ? '1em' : undefined,
            bottom: dragState.current.ox < 0 ? '3em' : undefined,
            width: '30em', zIndex: 2147483646,
            background: '#1a1a2e', border: '1px solid #35355a',
            borderRadius: '0.5em',
            boxShadow: '0 1em 3em rgba(0,0,0,0.6)',
            display: 'flex', flexDirection: 'column',
            overflow: 'hidden',
            fontFamily: 'var(--font-sans, system-ui)',
          }}
        >
          {/* ── Header / Drag ─────────────────────────────────────── */}
          <div
            onPointerDown={onDragStart}
            onPointerMove={onDragMove}
            onPointerUp={onDragEnd}
            style={{
              padding: '0.5em 0.75em',
              background: '#12122a',
              borderBottom: '1px solid #35355a',
              cursor: 'grab',
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              userSelect: 'none',
            }}
          >
            <span style={{ fontSize: '0.6875em', fontWeight: 700, color: '#7878aa', letterSpacing: '0.06em', textTransform: 'uppercase' }}>
              SASS Editor
            </span>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5em' }}>
              <span style={{ fontSize: '0.625em', color: '#44446a' }}>Ctrl+Shift+E</span>
              <button onClick={() => setIsOpen(false)}
                style={{ background: 'none', border: 'none', color: '#55557a', cursor: 'pointer', fontSize: '0.875em', padding: '0 0.125em' }}>
                ✕
              </button>
            </div>
          </div>

          {/* ── Datei-Auswahl ─────────────────────────────────────── */}
          <div style={{
            padding: '0.4em 0.6em',
            background: '#16162a',
            borderBottom: '1px solid #2a2a48',
          }}>
            <select
              value={selectedFile}
              onChange={e => setSelectedFile(e.target.value)}
              style={{
                width: '100%', padding: '0.3em 0.5em',
                borderRadius: '0.25em', border: '1px solid #35355a',
                background: '#222240', color: '#a0a0cc',
                fontSize: '0.625em', fontFamily: 'var(--font-mono, monospace)',
                cursor: 'pointer', outline: 'none',
              }}
            >
              {CSS_FILES.map(f => (
                <option key={f.path} value={f.path}>{f.path}</option>
              ))}
            </select>
          </div>

          {/* ── Textarea (SCSS) ───────────────────────────────────── */}
          <textarea
            value={scss}
            onChange={e => setScss(e.target.value)}
            onKeyDown={handleTab}
            spellCheck={false}
            placeholder={
`.szo__title\n  font-size: 1.2em\n  color: #fff\n\n` +
`// Variablen + Nesting:\n$accent: #6366f1\n\n` +
`.szo\n  background: #1a1a2e\n  &__header\n    padding: 1em\n  &:hover\n    opacity: 0.95`
            }
            style={{
              width: '100%', height: '22em', boxSizing: 'border-box',
              padding: '0.75em', background: '#1a1a2e', color: '#c8c8f0',
              border: 'none', outline: 'none',
              fontFamily: 'var(--font-mono, monospace)',
              fontSize: '0.75em', lineHeight: 1.65,
              resize: 'vertical', tabSize: 2,
            }}
          />

          {/* ── SCSS-Fehler ───────────────────────────────────────── */}
          {scssError && (
            <div style={{
              padding: '0.4em 0.75em', background: '#2a1010',
              borderTop: '1px solid #5a2020', color: '#f87171',
              fontSize: '0.625em', fontFamily: 'var(--font-mono, monospace)',
              whiteSpace: 'pre-wrap',
            }}>
              ✗ {scssError}
            </div>
          )}

          {/* ── Status-Bar ────────────────────────────────────────── */}
          {applyMsg && (
            <div style={{
              padding: '0.35em 0.75em',
              background: applyStatus === 'success' ? '#0a1f14' : '#2a1010',
              borderTop: `1px solid ${applyStatus === 'success' ? '#1a4a2a' : '#5a2020'}`,
              color: applyStatus === 'success' ? '#4ade80' : '#f87171',
              fontSize: '0.625em',
            }}>
              {applyMsg}
            </div>
          )}

          {/* ── Footer ────────────────────────────────────────────── */}
          <div style={{
            display: 'flex', gap: '0.4em', padding: '0.5em 0.75em',
            borderTop: '1px solid #35355a', background: '#12122a',
          }}>
            {/* Kopieren */}
            <button onClick={handleCopy} style={{
              flex: 1, padding: '0.35em 0',
              borderRadius: '0.3em', border: '1px solid #35355a',
              background: copied ? '#0f2a1a' : '#222240',
              color: copied ? '#4ade80' : '#7878aa',
              fontSize: '0.6875em', fontWeight: 600, cursor: 'pointer',
              transition: 'all 0.2s ease',
            }}>
              {copied ? '✓ Kopiert' : 'Kopieren'}
            </button>

            {/* Übernehmen */}
            <button
              onClick={handleApply}
              disabled={applyStatus === 'loading' || !!scssError || !scss.trim()}
              style={{
                flex: 2, padding: '0.35em 0',
                borderRadius: '0.3em', border: '1px solid #35355a',
                background: applyStatus === 'success' ? '#0f2a1a'
                           : applyStatus === 'error'   ? '#2a1010'
                           : '#222240',
                color: applyStatus === 'success' ? '#4ade80'
                      : applyStatus === 'error'   ? '#f87171'
                      : applyStatus === 'loading' ? '#55557a'
                      : '#a0a0cc',
                fontSize: '0.6875em', fontWeight: 600, cursor: 'pointer',
                transition: 'all 0.2s ease',
                opacity: (!!scssError || !scss.trim()) ? 0.4 : 1,
              }}
            >
              {applyStatus === 'loading' ? 'Schreibe …' : 'Übernehmen'}
            </button>
          </div>
        </div>
      )}
    </>,
    document.body
  )
}
